import React from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Checkbox,
  type SelectChangeEvent,
} from '@mui/material'
import type { CreateGlobalIngestKeyPayload, IngestKeyDefaultStatus } from '../../types/ingest'

interface CreateIngestKeyModalProps {
  open: boolean
  onClose: () => void
  onSubmit: (payload: CreateGlobalIngestKeyPayload) => Promise<unknown>
}

const DEFAULT_RATE_LIMIT = 10

const CreateIngestKeyModal: React.FC<CreateIngestKeyModalProps> = ({ open, onClose, onSubmit }) => {
  const [label, setLabel] = React.useState('')
  const [defaultStatus, setDefaultStatus] = React.useState<IngestKeyDefaultStatus>('published')
  const [allowAutoCategories, setAllowAutoCategories] = React.useState(true)
  const [rateLimit, setRateLimit] = React.useState<number>(DEFAULT_RATE_LIMIT)
  const [errors, setErrors] = React.useState<Record<string, string>>({})
  const [serverError, setServerError] = React.useState<string | null>(null)
  const [submitting, setSubmitting] = React.useState(false)

  React.useEffect(() => {
    if (!open) {
      return
    }

    setLabel('')
    setDefaultStatus('published')
    setAllowAutoCategories(true)
    setRateLimit(DEFAULT_RATE_LIMIT)
    setErrors({})
    setServerError(null)
    setSubmitting(false)
  }, [open])

  const validate = React.useCallback(() => {
    const nextErrors: Record<string, string> = {}

    if (!label.trim()) {
      nextErrors.label = 'A label is required.'
    }

    if (Number.isNaN(rateLimit) || rateLimit < 1 || rateLimit > 120) {
      nextErrors.rate_limit_per_min = 'Rate limit must be between 1 and 120.'
    }

    setErrors(nextErrors)

    return Object.keys(nextErrors).length === 0
  }, [label, rateLimit])

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    if (submitting) {
      return
    }

    if (!validate()) {
      return
    }

    setSubmitting(true)
    setServerError(null)

    const payload: CreateGlobalIngestKeyPayload = {
      label: label.trim(),
      policy: {
        default_status: defaultStatus,
        allow_auto_create_categories: allowAutoCategories,
        rate_limit_per_min: Math.min(Math.max(Math.round(rateLimit), 1), 120),
      },
    }

    try {
      await onSubmit(payload)
      onClose()
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to create ingest key.'
      setServerError(message)
    } finally {
      setSubmitting(false)
    }
  }

  const handleStatusChange = (event: SelectChangeEvent<IngestKeyDefaultStatus>) => {
    setDefaultStatus(event.target.value as IngestKeyDefaultStatus)
  }

  const handleRateLimitChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number.parseInt(event.target.value, 10)
    if (Number.isNaN(value)) {
      setRateLimit(NaN)
      return
    }
    setRateLimit(value)
  }

  const handleClose = () => {
    if (submitting) {
      return
    }
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Create a global ingest key</DialogTitle>
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <DialogContent dividers>
          <Stack spacing={3}>
            <TextField
              label="Label"
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              required
              autoFocus
              error={Boolean(errors.label)}
              helperText={errors.label}
              fullWidth
            />
            <FormControl fullWidth error={Boolean(errors.default_status)}>
              <InputLabel id="ingest-key-default-status">Default status</InputLabel>
              <Select
                labelId="ingest-key-default-status"
                value={defaultStatus}
                label="Default status"
                onChange={handleStatusChange}
              >
                <MenuItem value="published">Published</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
              </Select>
              {errors.default_status ? <FormHelperText>{errors.default_status}</FormHelperText> : null}
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={allowAutoCategories}
                  onChange={(event) => setAllowAutoCategories(event.target.checked)}
                />
              }
              label="Allow automatic creation of categories"
            />
            <TextField
              label="Rate limit per minute"
              type="number"
              inputProps={{ min: 1, max: 120 }}
              value={Number.isNaN(rateLimit) ? '' : rateLimit}
              onChange={handleRateLimitChange}
              error={Boolean(errors.rate_limit_per_min)}
              helperText={errors.rate_limit_per_min ?? 'Clamp between 1 and 120 requests per minute.'}
              fullWidth
            />
            {serverError ? <Alert severity="error">{serverError}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="inherit" disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={submitting}>
            {submitting ? 'Creating…' : 'Create key'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  )
}

export default CreateIngestKeyModal
